import { memo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Divider,
  CircularProgress,
  Button,
  useTheme,
  alpha,
  Chip,
  Fade,
  Grow,
} from "@mui/material";
import {
  NotificationsActive,
  Visibility,
  NotificationsOff,
} from "@mui/icons-material";
import RecentItem from "./RecentItem";

const RecentAlertsPanel = memo(
  ({
    recentAlerts,
    isDataLoaded,
    loading = true,
    refreshing,
    onDeviceClick,
  }) => {
    const theme = useTheme();
    const { t } = useTranslation("common");
    const navigate = useNavigate();

    // Show loading if explicitly loading or if data hasn't been loaded yet
    const showLoading = loading || !isDataLoaded;

    // Count alerts by severity for the header badge
    const alertCount = recentAlerts?.length || 0;
    const hasCritical = recentAlerts?.some(
      (a) => a.severity?.toLowerCase() === "critical"
    );

    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(145deg, rgba(40,40,45,0.9) 0%, rgba(35,35,40,0.8) 100%)"
              : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)",
          backdropFilter: "blur(10px)",
          border: `1px solid ${
            theme.palette.mode === "dark"
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)"
          }`,
          minHeight: 400,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Top accent bar */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: theme.palette.primary.main,
            opacity: 0.8,
          }}
        />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          mt={1}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 40,
                height: 40,
                borderRadius: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <NotificationsActive />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={600}>
                  {t("common.recent")}
                </Typography>
                {alertCount > 0 && (
                  <Chip
                    label={alertCount}
                    size="small"
                    sx={{
                      height: 22,
                      minWidth: 28,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      backgroundColor: hasCritical
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                      color: "#fff",
                    }}
                  />
                )}
                {refreshing && (
                  <CircularProgress
                    size={16}
                    thickness={4}
                    sx={{ color: theme.palette.text.secondary, ml: 0.5 }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {alertCount > 0
                  ? t("common.most_recent_alerts")
                  : t("common.all_clear", { defaultValue: "All clear" })}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={() => navigate("/alerts")}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                borderColor: alpha(theme.palette.divider, 0.3),
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                },
              }}
            >
              {t("common.view_all")}
            </Button>
          </Box>
        </Box>
        <Divider sx={{ mb: 2, opacity: 0.5 }} />

        {showLoading ? (
          <Box>
            {[...Array(5)].map((_, i) => (
              <Fade in timeout={300 + i * 100} key={i}>
                <Card
                  elevation={0}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center">
                      <Skeleton
                        variant="rounded"
                        width={32}
                        height={32}
                        sx={{ mr: 2, borderRadius: 1 }}
                      />
                      <Box flex={1}>
                        <Skeleton variant="text" width="70%" height={20} />
                        <Skeleton variant="text" width="40%" height={16} />
                      </Box>
                      <Skeleton
                        variant="rounded"
                        width={60}
                        height={24}
                        sx={{ borderRadius: 2 }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Fade>
            ))}
          </Box>
        ) : recentAlerts.length > 0 ? (
          <Box
            sx={{
              maxHeight: 400,
              overflowY: "auto",
              pr: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              "&::-webkit-scrollbar": {
                width: 6,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.secondary, 0.2),
                borderRadius: 3,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.3),
                },
              },
            }}
          >
            {recentAlerts.map((alert, index) => (
              <Grow
                in
                timeout={300 + index * 50}
                key={`alert-${alert.id || index}`}
              >
                <Box>
                  <RecentItem
                    timestamp={alert.time_created}
                    title={alert.message || "Alert triggered"}
                    subtitle={alert.deviceName}
                    severity={alert.severity || "info"}
                    type="alert"
                    deviceId={alert.deviceId}
                    onItemClick={onDeviceClick}
                  />
                </Box>
              </Grow>
            ))}
          </Box>
        ) : (
          <Fade in timeout={500}>
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py={8}
              sx={{
                background: alpha(theme.palette.success.main, 0.03),
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <NotificationsOff
                  sx={{
                    fontSize: 40,
                    color: theme.palette.success.main,
                  }}
                />
              </Box>
              <Typography
                color="text.primary"
                textAlign="center"
                variant="h6"
                fontWeight={600}
                gutterBottom
              >
                {t("common.no_recent_alerts")}
              </Typography>
              <Typography
                color="text.secondary"
                textAlign="center"
                variant="body2"
                sx={{ maxWidth: 280 }}
              >
                {t("common.recent_alerts_here")}.
              </Typography>
              <Button
                variant="text"
                size="small"
                onClick={() => navigate("/alerts")}
                sx={{ mt: 2, textTransform: "none" }}
              >
                {t("common.view_alert_history", {
                  defaultValue: "View alert history",
                })}
              </Button>
            </Box>
          </Fade>
        )}
      </Paper>
    );
  }
);

RecentAlertsPanel.displayName = "RecentAlertsPanel";

export default RecentAlertsPanel;
